import { useState, type FormEvent } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { UserStatus } from '@teentack/shared';
import { post } from '@/lib/api-client';
import { useAuth } from '@/auth/use-auth';
import { Card, ErrorNote, Field, InfoNote, SubmitButton } from '@/components/Form';

/** [FR-M01.1] No account is usable until a channel is OTP-verified. */
export function VerifyOtpPage() {
  const { verifyOtp } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();

  const identifier = params.get('identifier') ?? '';
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    setError(null);
    setBusy(true);

    try {
      const user = await verifyOtp(identifier, code);
      // Verifying signs you in, so an active account goes straight to its
      // profile. A minor still needs their guardian and lands on the holding
      // page that says so. [FR-M01.2]
      navigate(user.status === UserStatus.ACTIVE ? '/profile' : '/pending', {
        state: { justVerified: true },
      });
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Could not verify the code');
    } finally {
      setBusy(false);
    }
  }

  async function resend() {
    setError(null);
    setNotice(null);
    try {
      await post<unknown>('/auth/resend-otp', { identifier }, { auth: false });
      setNotice('A new code is on its way — check your inbox.');
      setCode('');
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Could not resend the code');
    }
  }

  return (
    <form onSubmit={handleSubmit}>
      <Card title="Enter your code" subtitle={`We sent a 6-digit code to ${identifier}`}>
        <ErrorNote message={error} />
        {notice && <InfoNote>{notice}</InfoNote>}

        <Field
          label="Verification code"
          value={code}
          onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
          inputMode="numeric"
          autoComplete="one-time-code"
          placeholder="000000"
          maxLength={6}
          autoFocus
          required
        />
        <SubmitButton busy={busy}>Verify</SubmitButton>

        <div className="flex justify-between text-sm">
          <button type="button" onClick={resend} className="text-brand-600 hover:underline">
            Resend code
          </button>
          <Link to="/login" className="text-slate-500 hover:underline">
            Back to sign in
          </Link>
        </div>
      </Card>
    </form>
  );
}
